import { ContentMetadataText } from '@quebon/shared-learning/components/common/ContentMetadataText'
import { ArrowRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { ChapterReportView } from '@/types/chapterReport'
import { CorrectRateMetric, UnderstandingMetric } from './ReportMetrics'

function UnderstandingChange({ initial, latest }: { initial: number | null; latest: number | null }) {
  return (
    <span className="inline-flex items-center gap-2 whitespace-nowrap">
      <UnderstandingMetric value={initial} muted align="start" />
      <ArrowRight size={10} className="text-[#4599ff]" aria-hidden="true" />
      <UnderstandingMetric value={latest} align="start" />
    </span>
  )
}

function ReviewResult({
  initialUnderstanding,
  latestUnderstanding,
  initialCorrectRate,
  latestCorrectRate,
}: {
  initialUnderstanding: number | null
  latestUnderstanding: number | null
  initialCorrectRate: number | null
  latestCorrectRate: number | null
}) {
  const { t } = useTranslation('coaching')
  const isPending = latestUnderstanding == null || latestCorrectRate == null
  const isComplete = !isPending && latestUnderstanding === 5 && latestCorrectRate >= 80
  const isGrowth = !isPending
    && !isComplete
    && initialUnderstanding != null
    && initialCorrectRate != null
    && latestUnderstanding > initialUnderstanding
    && latestCorrectRate > initialCorrectRate
  const label = isPending
    ? t('chapterReport.reviewResult.pending')
    : isComplete
      ? t('chapterReport.reviewResult.complete')
      : isGrowth
        ? t('chapterReport.reviewResult.growth')
        : t('chapterReport.reviewResult.supplement')
  const color = isComplete ? '#2ac47e' : isGrowth ? '#4599ff' : '#aeaeae'

  return (
    <span
      className="inline-flex min-h-8 min-w-8 items-center justify-center whitespace-nowrap rounded-full px-2 text-xs font-semibold text-white"
      style={{ backgroundColor: color }}
    >
      {label}
    </span>
  )
}

export function CompletedReviewSection({ report }: { report: ChapterReportView }) {
  const { t } = useTranslation('coaching')

  return (
    <section className="chapter-report-print-section space-y-6" data-testid="completed-review-section">
      <div className="space-y-2 px-2">
        <p className="text-base font-bold leading-[1.4] text-[#131313]">
          {t('chapterReport.masteryLead')}
        </p>
        <p className="text-xl font-bold leading-[1.4] tracking-[-0.6px] text-[#131313]">
          {t('chapterReport.masteryBefore')}
          <span className="text-[#4599ff]">
            {t('chapterReport.masteryCount', { count: report.completedReviews.length })}
          </span>
          {t('chapterReport.masteryAfter')}
        </p>
        <div className="flex items-center gap-2 text-xs leading-[1.5]">
          <span className="grid size-3.5 shrink-0 place-items-center rounded-full bg-[#eee] text-[10px] text-[#888]">?</span>
          <strong className="text-[#888]">{t('chapterReport.masteryWhat')}</strong>
          <span className="text-[#333]">{t('chapterReport.masteryHelp')}</span>
        </div>
      </div>
      {report.completedReviews.length === 0 ? (
        <div className="rounded-xl bg-[#f5f5f5] px-5 py-6 text-center text-xs text-[#888]">
          {t('chapterReport.completedReviewsEmpty')}
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-[#eaeaea]">
          <table className="w-full table-fixed text-left text-xs">
            <thead className="bg-[#f5f5f5] text-[#888]">
              <tr>
                <th className="w-[20%] px-4 py-3 font-medium">{t('chapterReport.reviewChapter')}</th>
                <th className="w-[35%] px-3 py-3 font-medium">{t('chapterReport.reviewLesson')}</th>
                <th className="w-[23%] px-2 py-3 text-center font-medium">{t('chapterReport.understandingChange')}</th>
                <th className="w-[12%] px-2 py-3 text-center font-medium">{t('chapterReport.correctRateChange')}</th>
                <th className="px-2 py-3 text-center font-medium">{t('chapterReport.result')}</th>
              </tr>
            </thead>
            <tbody>
              {report.completedReviews.map((review, index) => {
                const delta = review.initialCorrectRate != null && review.latestCorrectRate != null
                  ? review.latestCorrectRate - review.initialCorrectRate
                  : null
                const deltaColor = delta == null || delta === 0
                  ? '#888'
                  : delta > 0
                    ? '#2ac47e'
                    : '#ff675e'
                return (
                <tr
                  key={review.lessonId}
                  className={`chapter-report-row border-t border-[#eaeaea] ${index % 2 ? 'bg-[#f5f5f5]' : 'bg-white'}`}
                >
                  <td className="px-4 py-2 font-semibold text-[#888]">
                    {review.chapterTitle || t('chapterReport.missing')}
                  </td>
                  <td className="px-3 py-2 font-semibold text-[#131313]">
                    {review.lessonNumber != null && `${review.lessonNumber}. `}
                    <ContentMetadataText text={review.lessonTitle} />
                  </td>
                  <td className="px-2 py-2">
                    <UnderstandingChange
                      initial={review.initialUnderstanding}
                      latest={review.latestUnderstanding}
                    />
                  </td>
                  <td className="px-2 py-2 text-center">
                    <CorrectRateMetric value={review.latestCorrectRate} compact />
                    {delta != null && delta !== 0 && (
                      <p className="text-[10px] font-medium" style={{ color: deltaColor }}>
                        {delta > 0 ? '▲' : '▼'} {Math.abs(delta)}
                      </p>
                    )}
                  </td>
                  <td className="px-2 py-2">
                    <div className="flex justify-center">
                      <ReviewResult
                        initialUnderstanding={review.initialUnderstanding}
                        latestUnderstanding={review.latestUnderstanding}
                        initialCorrectRate={review.initialCorrectRate}
                        latestCorrectRate={review.latestCorrectRate}
                      />
                    </div>
                  </td>
                </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}
